"use client"

import type React from "react"
import { useState, useEffect, useCallback, useMemo } from "react"
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
  Spinner,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Alert,
  FormFeedback,
  InputGroup,
  InputGroupText,
} from "reactstrap"
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaUser,
  FaCamera,
  FaFileAlt,
  FaExclamationTriangle,
  FaDollarSign,
  FaInfoCircle,
  FaUserPlus,
  FaSync,
  FaIdCard,
  FaEnvelope,
  FaPhone,
  FaUserCheck,
  FaSearch,
  FaCheckSquare,
  FaSquare,
} from "react-icons/fa"
import Swal from "sweetalert2"

// ─── Types ───────────────────────────────────────────────────────────────────

interface Staff {
  id: number
  person_id: number
  person_full_name: string
  person_email: string
  person_phone: string
  person_id_number: string
  role: string
  exhibitor_name: string
  exhibition_title: string
  stand_category: string
  profile_photo_url: string | null
  has_profile_photo: boolean
  total_badges_printed: number
  has_original_badge: boolean
  can_print_original: boolean
  status: boolean
  created_at: string
  participation_id: number | null
}

interface StaffResponse {
  success: boolean
  staff: Staff[]
  summary: {
    total_staff: number
    filtered_count: number
    staff_with_badges: number
    original_badges: number
    copy_badges: number
    total_badges: number
    max_representatives: number
    excess_staff: number
    badge_fee: number
    excess_fee: number
    currency: string
    can_add_more: boolean
    exhibition_title: string
    stand_category_name: string
    participation_id: number | null
    can_self_register?: boolean
    user_already_registered?: boolean
  }
  debug?: any
}

interface IDDocumentType {
  id: number
  name: string
}

interface ValidationErrors {
  [key: string]: string | string[]
}

interface PreviousRepresentative {
  person_id: number
  person_full_name: string
  person_email: string
  person_phone: string
  person_id_number: string
  last_role: string
  last_participation_exhibition: string
  profile_photo_url: string | null
  has_profile_photo: boolean
}

interface StaffManagementProps {
  exhibitor: any
  participation: any
  onRefresh?: () => void
}

// ─── Role options (shared) ────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  "Representative",
  "Exhibitor",
  "Official",
  "Organizer",
  "Manager",
  "Marketing Manager",
  "Technical Support",
  "Customer Service",
  "Executive",
  "Coordinator",
  "Specialist",
  "Registrar",
  "Administrator",
  "Other",
]

// ─── Component ────────────────────────────────────────────────────────────────

const EnhancedStaffManagement: React.FC<StaffManagementProps> = ({ exhibitor, participation, onRefresh }) => {
  // ── Existing state ──────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false)
  const [selfRegistrationModalOpen, setSelfRegistrationModalOpen] = useState(false)
  const [staffList, setStaffList] = useState<Staff[]>([])
  const [summary, setSummary] = useState<StaffResponse["summary"] | null>(null)
  const [idDocTypes, setIdDocTypes] = useState<IDDocumentType[]>([])
  const [form, setForm] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    person_email: "",
    person_phone: "",
    person_id_number: "",
    role: "",
    id_doc_type: "",
    photo: null as File | null,
    id_doc_attachment: null as File | null,
    remove_photo: false,
    remove_id_doc: false,
    acknowledge_excess_fee: false,
  })
  const [selfRegistrationForm, setSelfRegistrationForm] = useState({ role: "Representative" })
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null)
  const [loading, setLoading] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [selfRegistrationLoading, setSelfRegistrationLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [excessFeeWarning, setExcessFeeWarning] = useState<any>(null)
  const [debugInfo, setDebugInfo] = useState<any>(null)
  const [currentFiles, setCurrentFiles] = useState({
    has_photo: false,
    has_id_doc: false,
    photo_url: null as string | null,
    id_doc_url: null as string | null,
  })

  // ── NEW: Select-from-existing state ────────────────────────────────────────
  const [selectExistingModalOpen, setSelectExistingModalOpen] = useState(false)
  const [previousReps, setPreviousReps] = useState<PreviousRepresentative[]>([])
  const [previousRepsSearch, setPreviousRepsSearch] = useState("")
  const [selectedPersonIds, setSelectedPersonIds] = useState<Set<number>>(new Set())
  const [assignRole, setAssignRole] = useState("Representative")
  const [loadingPreviousReps, setLoadingPreviousReps] = useState(false)
  const [assignLoading, setAssignLoading] = useState(false)
  const [acknowledgeExcessFee, setAcknowledgeExcessFee] = useState(false)

  // ── NEW: Frontend-only search over the participants table ──────────────────
  // Filters the already-fetched `staffList` in memory — no API call, no
  // refetch, just a client-side match against name/email/phone/ID/role/
  // exhibitor fields. Recomputes only when the list or the term changes.
  const [staffSearchTerm, setStaffSearchTerm] = useState("")

  const filteredStaffList = useMemo(() => {
    const term = staffSearchTerm.trim().toLowerCase()
    if (!term) return staffList
    return staffList.filter((staff) => {
      const haystack = [
        staff.person_full_name,
        staff.person_email,
        staff.person_phone,
        staff.person_id_number,
        staff.role,
        staff.exhibitor_name,
        staff.exhibition_title,
        staff.stand_category,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return haystack.includes(term)
    })
  }, [staffList, staffSearchTerm])

  const baseURL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"

  // ─── Fetch helpers ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (exhibitor?.id) {
      fetchStaff()
      loadIdDocTypes()
    }
  }, [exhibitor?.id, participation?.id])

  const loadIdDocTypes = async () => {
    try {
      const token = localStorage.getItem("accessToken")
      const response = await fetch(`${baseURL}/api/accounts/get-id-document-types/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) setIdDocTypes(await response.json())
    } catch (err) {
      console.error("Error loading ID document types:", err)
    }
  }

  const fetchStaff = async () => {
    if (!exhibitor?.id) return
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("accessToken")
      let url = `${baseURL}/api/exhibitorsaccess/exhibitors/${exhibitor.id}/representations/`
      const params = new URLSearchParams()
      if (participation?.id) params.append("participation_id", participation.id.toString())
      if (params.toString()) url += `?${params.toString()}`
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) throw new Error(`HTTP ${response.status}: Failed to fetch participant`)
      const data: StaffResponse = await response.json()
      if (data.success) {
        setStaffList(data.staff || [])
        setSummary(data.summary)
        setDebugInfo(data.debug)
      } else {
        throw new Error("API returned unsuccessful response")
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch staff data")
      Swal.fire({ title: "Error!", text: `Failed to fetch staff: ${err.message}`, icon: "error", confirmButtonColor: "#dc3545" })
    } finally {
      setLoading(false)
    }
  }

  /** Fetch persons from previous participations of this exhibitor, excluding
   *  those already in the current participation. */
  const fetchPreviousRepresentatives = useCallback(async (search = "") => {
    if (!exhibitor?.id) return
    setLoadingPreviousReps(true)
    try {
      const token = localStorage.getItem("accessToken")
      const params = new URLSearchParams()
      const currentParticipationId = participation?.id ?? summary?.participation_id
      if (currentParticipationId) params.append("exclude_participation_id", String(currentParticipationId))
      if (search.trim()) params.append("search", search.trim())
      const url = `${baseURL}/api/exhibitorsaccess/exhibitors/${exhibitor.id}/previous-representatives/?${params}`
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) throw new Error("Failed to load previous representatives")
      const data = await response.json()
      setPreviousReps(data.representatives || [])
    } catch (err: any) {
      console.error("Error fetching previous reps:", err)
      setPreviousReps([])
    } finally {
      setLoadingPreviousReps(false)
    }
  }, [exhibitor?.id, participation?.id, summary?.participation_id, baseURL])

  const handleRefresh = async () => {
    await fetchStaff()
    if (onRefresh) onRefresh()
  }

  // ─── Self-registration ──────────────────────────────────────────────────────

  const handleSelfRegistration = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selfRegistrationForm.role.trim()) {
      Swal.fire({ title: "Validation Error", text: "Please select your role", icon: "error", confirmButtonColor: "#dc3545" })
      return
    }
    setSelfRegistrationLoading(true)
    try {
      const token = localStorage.getItem("accessToken")
      const response = await fetch(`${baseURL}/api/exhibitorsaccess/exhibitors/${exhibitor.id}/register-self/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role: selfRegistrationForm.role.trim() }),
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || "Failed to register yourself")
      }
      const result = await response.json()
      await Swal.fire({ title: "Registration Successful!", text: `Registered as "${selfRegistrationForm.role}" for ${exhibitor.name}.`, icon: "success", confirmButtonColor: "#28a745" })
      setSelfRegistrationModalOpen(false)
      setSelfRegistrationForm({ role: "Representative" })
      await fetchStaff()
      if (onRefresh) onRefresh()
    } catch (err: any) {
      await Swal.fire({ title: "Registration Failed", text: err.message || "Failed to register", icon: "error", confirmButtonColor: "#dc3545" })
    } finally {
      setSelfRegistrationLoading(false)
    }
  }

  // ─── Staff detail (for edit) ────────────────────────────────────────────────

  const fetchStaffDetail = async (staffId: number) => {
    setFormLoading(true)
    setError(null)
    setValidationErrors({})
    try {
      const token = localStorage.getItem("accessToken")
      const response = await fetch(`${baseURL}/api/exhibitorsaccess/staff/${staffId}/detail/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error("Failed to fetch participant details")
      const staffDetail = await response.json()
      const nameParts = staffDetail.person_full_name?.split(" ") || []
      setForm({
        first_name: staffDetail.first_name || nameParts[0] || "",
        middle_name: staffDetail.middle_name || (nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : ""),
        last_name: staffDetail.last_name || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : ""),
        person_email: staffDetail.email || "",
        person_phone: staffDetail.phone || "",
        person_id_number: staffDetail.id_doc_number || "",
        role: staffDetail.role || "",
        id_doc_type: staffDetail.id_doc_type?.toString() || "",
        photo: null,
        id_doc_attachment: null,
        remove_photo: false,
        remove_id_doc: false,
        acknowledge_excess_fee: false,
      })
      setCurrentFiles({
        has_photo: staffDetail.has_photo || false,
        has_id_doc: staffDetail.has_id_doc || false,
        photo_url: staffDetail.photo_url,
        id_doc_url: staffDetail.id_doc_url,
      })
    } catch (err: any) {
      setError(err.message || "Failed to load participant details")
    } finally {
      setFormLoading(false)
    }
  }

  // ─── Form handlers ──────────────────────────────────────────────────────────

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    if (validationErrors[name]) {
      setValidationErrors((prev) => { const n = { ...prev }; delete n[name]; return n })
    }
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }))
    } else {
      setForm((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target
    if (files && files[0]) {
      if (name === "photo" && validationErrors.photo) {
        setValidationErrors((prev) => { const n = { ...prev }; delete n.photo; return n })
      }
      setForm((prev) => ({ ...prev, [name]: files[0] }))
    }
  }

  const handleRemoveFile = (fileType: "photo" | "id_doc") => {
    if (fileType === "photo") {
      setForm((prev) => ({ ...prev, photo: null, remove_photo: true }))
      setCurrentFiles((prev) => ({ ...prev, has_photo: false, photo_url: null }))
    } else {
      setForm((prev) => ({ ...prev, id_doc_attachment: null, remove_id_doc: true }))
      setCurrentFiles((prev) => ({ ...prev, has_id_doc: false, id_doc_url: null }))
    }
  }

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {}
    if (!form.first_name.trim()) errors.first_name = "First name is required"
    if (!form.last_name.trim()) errors.last_name = "Last name is required"
    if (!form.person_email.trim()) {
      errors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.person_email.trim())) {
      errors.email = "Please enter a valid email address"
    }
    if (!form.person_phone.trim()) errors.phone = "Phone number is required"
    if (!form.id_doc_type) errors.id_doc_type = "ID document type is required"
    if (!form.person_id_number.trim()) errors.id_doc_number = "ID document number is required"
    if (!editingStaff && !form.photo) errors.photo = "Profile photo is required"
    if (editingStaff && !currentFiles.has_photo && !form.photo && form.remove_photo) errors.photo = "Profile photo is required"
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) { setError("Please fill in all required fields correctly"); return }
    setFormLoading(true)
    setError(null)
    setValidationErrors({})
    setExcessFeeWarning(null)
    try {
      const token = localStorage.getItem("accessToken")
      const formData = new FormData()
      if (editingStaff) {
        Object.keys(form).forEach((key) => {
          const value = form[key as keyof typeof form]
          if (value !== null && value !== undefined && value !== "") {
            if (key === "photo" || key === "id_doc_attachment") {
              if (value instanceof File) formData.append(key, value)
            } else if (key === "remove_photo" || key === "remove_id_doc" || key === "acknowledge_excess_fee") {
              formData.append(key, String(value))
            } else {
              formData.append(key, String(value))
            }
          }
        })
        const response = await fetch(`${baseURL}/api/exhibitorsaccess/staff/${editingStaff.id}/update/`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        })
        if (!response.ok) {
          const errorData = await response.json()
          if (response.status === 400 && errorData && typeof errorData === "object" && !errorData.error) {
            setValidationErrors(errorData); return
          }
          setError(errorData.error || "Failed to update participant."); return
        }
      } else {
        const registrationData: any = {
          exhibitor_id: exhibitor.id,
          participation_id: participation?.id || summary?.participation_id,
          is_self: false,
          first_name: form.first_name,
          middle_name: form.middle_name,
          last_name: form.last_name,
          email: form.person_email,
          phone: form.person_phone,
          id_doc_type: form.id_doc_type ? parseInt(form.id_doc_type) : undefined,
          id_doc_number: form.person_id_number,
          acknowledge_excess_fee: form.acknowledge_excess_fee,
        }
        Object.keys(registrationData).forEach((key) => {
          const value = registrationData[key]
          if (value !== null && value !== undefined && value !== "") formData.append(key, String(value))
        })
        if (form.photo) formData.append("photo", form.photo)
        if (form.id_doc_attachment) formData.append("id_doc_attachment", form.id_doc_attachment)
        const response = await fetch(`${baseURL}/api/exhibitorsaccess/staff/register/`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        })
        if (!response.ok) {
          const errorData = await response.json()
          if (errorData.excess_fee_required) { setExcessFeeWarning(errorData); return }
          if (response.status === 400 && errorData && typeof errorData === "object" && !errorData.error) {
            setValidationErrors(errorData); return
          }
          setError(errorData.error || "Failed to add participant."); return
        }
      }
      await fetchStaff()
      toggleModal()
      if (onRefresh) onRefresh()
      Swal.fire({ title: "Success!", text: `Participant ${editingStaff ? "updated" : "added"} successfully.`, icon: "success", confirmButtonColor: "#28a745" })
    } catch (err: any) {
      setError("Failed to save participant. Please try again.")
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = (id: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true)
        try {
          const token = localStorage.getItem("accessToken")
          const response = await fetch(`${baseURL}/api/exhibitorsaccess/staff/${id}/remove/`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          })
          if (!response.ok) throw new Error("Failed to delete staff")
          await fetchStaff()
          if (onRefresh) onRefresh()
          Swal.fire("Deleted!", "Participant has been removed.", "success")
        } catch (err: any) {
          Swal.fire({ title: "Error!", text: err.message || "Failed to delete participant.", icon: "error", confirmButtonColor: "#dc3545" })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  // ─── Modal helpers ──────────────────────────────────────────────────────────

  const resetForm = () => {
    setForm({ first_name: "", middle_name: "", last_name: "", person_email: "", person_phone: "", person_id_number: "", role: "", id_doc_type: "", photo: null, id_doc_attachment: null, remove_photo: false, remove_id_doc: false, acknowledge_excess_fee: false })
    setCurrentFiles({ has_photo: false, has_id_doc: false, photo_url: null, id_doc_url: null })
    setError(null)
    setValidationErrors({})
    setExcessFeeWarning(null)
    setEditingStaff(null)
  }

  const toggleModal = () => {
    setModalOpen((o) => !o)
    if (!modalOpen) resetForm()
  }

  const toggleSelfRegistrationModal = () => {
    setSelfRegistrationModalOpen((o) => !o)
    if (!selfRegistrationModalOpen) setSelfRegistrationForm({ role: "Representative" })
  }

  const handleEdit = async (staff: Staff) => {
    setEditingStaff(staff)
    setModalOpen(true)
    await fetchStaffDetail(staff.id)
  }

  const handleAddStaff = () => { resetForm(); setModalOpen(true) }

  // ─── Select-from-existing handlers ─────────────────────────────────────────

  const openSelectExistingModal = async () => {
    setSelectedPersonIds(new Set())
    setAssignRole("Representative")
    setPreviousRepsSearch("")
    setAcknowledgeExcessFee(false)
    setSelectExistingModalOpen(true)
    await fetchPreviousRepresentatives("")
  }

  const toggleSelectExistingModal = () => {
    setSelectExistingModalOpen((o) => !o)
  }

  const handlePreviousRepsSearch = async (value: string) => {
    setPreviousRepsSearch(value)
    await fetchPreviousRepresentatives(value)
  }

  const togglePersonSelection = (personId: number) => {
    setAcknowledgeExcessFee(false)
    setSelectedPersonIds((prev) => {
      const next = new Set(prev)
      if (next.has(personId)) next.delete(personId)
      else next.add(personId)
      return next
    })
  }

  const handleSelectAll = () => {
    setAcknowledgeExcessFee(false)
    setSelectedPersonIds(new Set(previousReps.map((r) => r.person_id)))
  }

  const handleDeselectAll = () => {
    setAcknowledgeExcessFee(false)
    setSelectedPersonIds(new Set())
  }

  const handleAssignExisting = async () => {
    if (selectedPersonIds.size === 0) {
      Swal.fire({ title: "No Selection", text: "Please select at least one representative.", icon: "warning", confirmButtonColor: "#f0ad4e" })
      return
    }
    const targetParticipationId = participation?.id ?? summary?.participation_id
    if (!targetParticipationId) {
      Swal.fire({ title: "Error", text: "No active participation found.", icon: "error", confirmButtonColor: "#dc3545" })
      return
    }
    setAssignLoading(true)
    try {
      const token = localStorage.getItem("accessToken")
      const response = await fetch(`${baseURL}/api/exhibitorsaccess/participations/${targetParticipationId}/assign-existing/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ person_ids: Array.from(selectedPersonIds), role: assignRole, acknowledge_excess_fee: acknowledgeExcessFee }),
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Failed to assign representatives")

      const skippedMsg = data.skipped_count > 0 ? `\n${data.skipped_count} already in this participation (skipped).` : ""
      const errorMsg = data.errors?.length > 0 ? `\n${data.errors.length} could not be added.` : ""
      await Swal.fire({
        title: "Done!",
        html: `<strong>${data.created_count}</strong> representative(s) added successfully.${skippedMsg}${errorMsg}`,
        icon: "success",
        confirmButtonColor: "#28a745",
      })
      setSelectExistingModalOpen(false)
      await fetchStaff()
      if (onRefresh) onRefresh()
    } catch (err: any) {
      Swal.fire({ title: "Error", text: err.message || "Failed to assign representatives.", icon: "error", confirmButtonColor: "#dc3545" })
    } finally {
      setAssignLoading(false)
    }
  }

  // ─── Badge status helpers ───────────────────────────────────────────────────

  const getBadgeStatusColor = (staff: Staff) => {
    if (staff.has_original_badge) return staff.can_print_original ? "success" : "primary"
    return "secondary"
  }

  const getBadgeStatusText = (staff: Staff) => {
    if (staff.has_original_badge) return staff.can_print_original ? "Can Print" : "Printed"
    return "No Badge"
  }

  const getValidationError = (fieldName: string): string => {
    const err = validationErrors[fieldName]
    return Array.isArray(err) ? err[0] : err || ""
  }

  const hasValidationError = (fieldName: string) => !!validationErrors[fieldName]

  const canSelfRegister = summary?.can_self_register !== false

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="staff-management">
      {/* Debug info (dev only) */}
      {debugInfo && process.env.NODE_ENV === "development" && (
        <Alert color="info" className="mb-3 p-2 small">
          <FaInfoCircle className="me-1" />
          <strong>Debug:</strong>
          <div className="d-flex flex-wrap mt-1 gap-2">
            <Badge color="secondary" className="fw-normal">Exhibitor: {debugInfo.exhibitor_id}</Badge>
            <Badge color="secondary" className="fw-normal">Participation: {debugInfo.participation_id || "None"}</Badge>
            <Badge color={debugInfo.participation_found ? "success" : "warning"} className="fw-normal">
              Participation: {debugInfo.participation_found ? "Yes" : "No"}
            </Badge>
          </div>
        </Alert>
      )}

      {/* Summary cards */}
      {summary ? (
        <Row className="mb-3 g-2">
          {[
            { label: "Total Participants", value: summary.total_staff, color: "primary" },
            { label: "Max Allowed", value: summary.max_representatives, color: summary.max_representatives > 0 ? "info" : "warning" },
            { label: "Excess", value: summary.excess_staff, color: summary.excess_staff > 0 ? "warning" : "success" },
            { label: "With Badges", value: summary.staff_with_badges, color: "success" },
            { label: "Original Badges", value: summary.original_badges, color: "info" },
            { label: "Excess Fee", value: `${summary.excess_fee.toLocaleString()} ${summary.currency}`, color: "danger" },
          ].map(({ label, value, color }) => (
            <Col key={label} xs={6} md={2}>
              <Card className={`h-100 border-0 shadow-sm border-top border-top-${color}`}>
                <CardBody className="p-3 text-center">
                  <h6 className={`text-${color} mb-1`}>{value}</h6>
                  <small className="text-muted">{label}</small>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Alert color="warning" className="mb-3 p-2 small">
          <FaExclamationTriangle className="me-1" />
          Unable to load participant summary. This might indicate a configuration issue with the participation or stand category.
        </Alert>
      )}

      {summary && summary.excess_staff > 0 && (
        <Alert color="info" className="mb-3 p-2 small">
          <FaExclamationTriangle className="me-1" />
          <strong>Excess Participant Notice:</strong> {summary.excess_staff} participant(s) exceed the limit of {summary.max_representatives}. Additional fee: {summary.excess_fee.toLocaleString()} {summary.currency}
        </Alert>
      )}

      {/* ── Staff table card ── */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="bg-light border-0 py-2 px-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h6 className="mb-0 small fw-semibold">
                <FaUser className="me-2" />
                Participants ({filteredStaffList.length}/{summary?.max_representatives ?? 0})
                {staffSearchTerm && (
                  <span className="text-muted fw-normal"> — {filteredStaffList.length} of {staffList.length} shown</span>
                )}
              </h6>
              {summary && (
                <small className="text-muted" style={{ fontSize: "0.72rem" }}>
                  {summary.exhibition_title} — {summary.stand_category_name}
                </small>
              )}
            </div>
            <div className="d-flex gap-1 align-items-center flex-wrap">
              {/* Client-side search box */}
              <InputGroup size="sm" style={{ width: 220 }}>
                <InputGroupText><FaSearch size={11} /></InputGroupText>
                <Input
                  type="text"
                  placeholder="Search participants…"
                  value={staffSearchTerm}
                  onChange={(e) => setStaffSearchTerm(e.target.value)}
                  bsSize="sm"
                />
                {staffSearchTerm && (
                  <Button color="outline-secondary" size="sm" onClick={() => setStaffSearchTerm("")} title="Clear search">
                    ×
                  </Button>
                )}
              </InputGroup>
              <Button color="outline-secondary" onClick={handleRefresh} size="sm" className="px-2" title="Refresh">
                <FaSync size={11} />
              </Button>
              {/* Select from existing button */}
              <Button
                color="outline-success"
                size="sm"
                className="d-flex align-items-center gap-1 px-2"
                onClick={openSelectExistingModal}
                title="Select from previous participations"
              >
                <FaUserCheck size={11} />
                <span className="d-none d-md-inline small">From Previous</span>
              </Button>
              <Button color="primary" onClick={handleAddStaff} size="sm" className="d-flex align-items-center gap-1 px-2">
                <FaPlus size={11} />
                <span className="small">Add New</span>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" size="sm" />
              <p className="mt-2 small text-muted mb-0">Loading participants…</p>
            </div>
          ) : staffList.length === 0 ? (
            <div className="text-center py-5">
              <FaUser size={28} className="text-muted mb-2" />
              <h6 className="text-muted small">No Participants</h6>
              <p className="text-muted small mb-3">Add participants to start generating badges.</p>
              <div className="d-flex gap-2 justify-content-center">
                <Button color="outline-success" size="sm" onClick={openSelectExistingModal}>
                  <FaUserCheck className="me-1" size={12} /> From Previous
                </Button>
                <Button color="primary" size="sm" onClick={handleAddStaff}>
                  <FaPlus className="me-1" size={12} /> Add New
                </Button>
              </div>
            </div>
          ) : filteredStaffList.length === 0 ? (
            <div className="text-center py-5">
              <FaSearch size={28} className="text-muted mb-2" />
              <h6 className="text-muted small">No Matches</h6>
              <p className="text-muted small mb-3">
                No participants match "<strong>{staffSearchTerm}</strong>".
              </p>
              <Button color="outline-secondary" size="sm" onClick={() => setStaffSearchTerm("")}>
                Clear Search
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table borderless hover className="mb-0" style={{ fontSize: "0.82rem" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f9fafb" }}>
                    <th className="ps-3 py-2 fw-medium text-muted border-bottom" style={{ width: 44 }}></th>
                    <th className="py-2 fw-medium text-muted border-bottom">Name</th>
                    <th className="py-2 fw-medium text-muted border-bottom">Contact</th>
                    <th className="py-2 fw-medium text-muted border-bottom text-center" style={{ width: 110 }}>Badge</th>
                    <th className="pe-3 py-2 fw-medium text-muted border-bottom text-end" style={{ width: 76 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaffList.map((staff) => (
                    <tr key={staff.id} className="align-middle">
                      <td className="ps-3">
                        {staff.has_profile_photo ? (
                          <img
                            src={staff.profile_photo_url || "/placeholder.svg"}
                            alt={staff.person_full_name}
                            className="rounded-circle"
                            style={{ width: 32, height: 32, objectFit: "cover" }}
                          />
                        ) : (
                          <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}>
                            <FaUser className="text-muted" size={12} />
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="fw-medium" style={{ lineHeight: 1.2 }}>{staff.person_full_name}</div>
                        <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                          <FaIdCard size={9} className="me-1" />{staff.person_id_number}
                        </div>
                      </td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: 180, fontSize: "0.78rem" }}>
                          <FaEnvelope size={9} className="me-1 text-muted" />{staff.person_email}
                        </div>
                        <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                          <FaPhone size={9} className="me-1" />{staff.person_phone}
                        </div>
                      </td>
                      <td className="text-center">
                        <Badge color={getBadgeStatusColor(staff)} pill style={{ fontSize: "0.68rem" }}>
                          {getBadgeStatusText(staff)}
                        </Badge>
                        {staff.total_badges_printed > 0 && (
                          <div className="text-muted mt-1" style={{ fontSize: "0.68rem" }}>
                            ×{staff.total_badges_printed}
                          </div>
                        )}
                      </td>
                      <td className="pe-3 text-end">
                        <div className="btn-group btn-group-sm" role="group">
                          <Button color="outline-primary" size="sm" className="px-2 py-1 border-0" onClick={() => handleEdit(staff)} title="Edit">
                            <FaEdit size={11} />
                          </Button>
                          <Button color="outline-danger" size="sm" className="px-2 py-1 border-0" onClick={() => handleDelete(staff.id)} title="Remove">
                            <FaTrash size={11} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ════════════════════════════════════════════════════════
          Modal: Select from Previous Participations
      ════════════════════════════════════════════════════════ */}
      {(() => {
        // ── Derived fee maths (computed inside render so they stay reactive) ──
        const selectedCount = selectedPersonIds.size
        const maxReps      = summary?.max_representatives ?? 0
        const currentCount = summary?.total_staff ?? 0
        const badgeFee     = summary?.badge_fee ?? 0
        const currency     = summary?.currency ?? ""
        const freeSlots    = Math.max(0, maxReps - currentCount)
        // How many of the newly selected people would push over the limit
        const newExcessCount = Math.max(0, selectedCount - freeSlots)
        const newExcessFee   = newExcessCount * badgeFee
        const willIncurFee   = newExcessCount > 0 && maxReps > 0
        // Footer button is enabled only when (a) something is selected AND
        // (b) if there's a fee the user must have acknowledged it
        const canSubmit = selectedCount > 0 && (!willIncurFee || acknowledgeExcessFee)

        return (
          <Modal isOpen={selectExistingModalOpen} toggle={toggleSelectExistingModal} size="lg" centered>
            <ModalHeader toggle={toggleSelectExistingModal} className="py-2 px-3">
              <FaUserCheck className="me-2" size={15} />
              <span className="fs-6">Select from Previous Representatives</span>
            </ModalHeader>

            <ModalBody className="p-3">
              {/* ── Pricing context bar (mirrors "Add New" modal) ── */}
              {summary && (
                <Alert color="info" className="p-2 mb-3 small d-flex align-items-start gap-2">
                  <FaInfoCircle className="mt-1 flex-shrink-0" size={13} />
                  <div>
                    <strong>Pricing: </strong>
                    {summary.exhibition_title} · {summary.stand_category_name} ·{" "}
                    Max {maxReps} · Current {currentCount} ·{" "}
                    Fee per excess: {badgeFee.toLocaleString()} {currency}
                    {currentCount >= maxReps && maxReps > 0 && (
                      <span className="text-warning fw-semibold"> — Quota is already full</span>
                    )}
                  </div>
                </Alert>
              )}

              {/* ── Instruction ── */}
              <p className="small text-muted mb-3">
                People who represented <strong>{exhibitor?.name}</strong> in other participations are listed below.
                Select one or more to add them to the current participation — no need to re-enter their details.
              </p>

              {/* ── Search + role row ── */}
              <Row className="g-2 mb-2">
                <Col md={7}>
                  <InputGroup size="sm">
                    <InputGroupText><FaSearch size={11} /></InputGroupText>
                    <Input
                      type="text"
                      placeholder="Search by name, email, phone or ID…"
                      value={previousRepsSearch}
                      onChange={(e) => handlePreviousRepsSearch(e.target.value)}
                      bsSize="sm"
                    />
                  </InputGroup>
                </Col>
                <Col md={5}>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={assignRole}
                    onChange={(e) => setAssignRole(e.target.value)}
                    title="Role to assign to all selected people"
                  >
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </Input>
                </Col>
              </Row>

              {/* ── Bulk-select controls + live count ── */}
              {previousReps.length > 0 && (
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <small className="text-muted fw-medium">
                    {selectedCount} of {previousReps.length} selected
                  </small>
                  <Button color="link" size="sm" className="p-0 text-decoration-none small" onClick={handleSelectAll}>
                    <FaCheckSquare size={11} className="me-1" />Select All
                  </Button>
                  <Button color="link" size="sm" className="p-0 text-decoration-none small text-secondary" onClick={handleDeselectAll}>
                    <FaSquare size={11} className="me-1" />Deselect All
                  </Button>
                  {/* Live slot breakdown */}
                  {selectedCount > 0 && maxReps > 0 && (
                    <span className="ms-auto small">
                      {Math.min(selectedCount, freeSlots) > 0 && (
                        <Badge color="success" pill className="me-1" style={{ fontSize: "0.68rem" }}>
                          {Math.min(selectedCount, freeSlots)} within quota
                        </Badge>
                      )}
                      {newExcessCount > 0 && (
                        <Badge color="warning" pill style={{ fontSize: "0.68rem" }}>
                          {newExcessCount} excess
                        </Badge>
                      )}
                    </span>
                  )}
                </div>
              )}

              {/* ── Representative list ── */}
              {loadingPreviousReps ? (
                <div className="text-center py-4">
                  <Spinner color="primary" size="sm" />
                  <p className="mt-2 small text-muted mb-0">Loading previous representatives…</p>
                </div>
              ) : previousReps.length === 0 ? (
                <div className="text-center py-4">
                  <FaUser size={28} className="text-muted mb-2" />
                  <p className="text-muted small mb-0">
                    {previousRepsSearch
                      ? "No previous representatives match your search."
                      : "No previous representatives found for this exhibitor."}
                  </p>
                </div>
              ) : (
                <div
                  className="border rounded"
                  style={{ maxHeight: 310, overflowY: "auto" }}
                >
                  <Table borderless hover size="sm" className="mb-0" style={{ fontSize: "0.82rem" }}>
                    <thead style={{ position: "sticky", top: 0, background: "#f9fafb", zIndex: 1 }}>
                      <tr>
                        <th className="ps-2 py-2 border-bottom" style={{ width: 36 }}></th>
                        <th className="py-2 border-bottom" style={{ width: 36 }}></th>
                        <th className="py-2 border-bottom">Name</th>
                        <th className="py-2 border-bottom">Contact</th>
                        <th className="py-2 border-bottom">Last Exhibition</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previousReps.map((rep) => {
                        const isSelected = selectedPersonIds.has(rep.person_id)
                        return (
                          <tr
                            key={rep.person_id}
                            className="align-middle"
                            style={{ cursor: "pointer", background: isSelected ? "#f0f7ff" : undefined }}
                            onClick={() => togglePersonSelection(rep.person_id)}
                          >
                            <td className="ps-2">
                              <Input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => togglePersonSelection(rep.person_id)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ cursor: "pointer" }}
                              />
                            </td>
                            <td>
                              {rep.has_profile_photo ? (
                                <img
                                  src={rep.profile_photo_url || "/placeholder.svg"}
                                  alt={rep.person_full_name}
                                  className="rounded-circle"
                                  style={{ width: 28, height: 28, objectFit: "cover" }}
                                />
                              ) : (
                                <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: 28, height: 28 }}>
                                  <FaUser className="text-muted" size={11} />
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="fw-medium" style={{ lineHeight: 1.2 }}>{rep.person_full_name}</div>
                              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                                <FaIdCard size={9} className="me-1" />{rep.person_id_number}
                              </div>
                            </td>
                            <td>
                              <div style={{ fontSize: "0.78rem" }}>
                                <FaEnvelope size={9} className="me-1 text-muted" />{rep.person_email}
                              </div>
                              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                                <FaPhone size={9} className="me-1" />{rep.person_phone}
                              </div>
                            </td>
                            <td>
                              <div style={{ fontSize: "0.75rem" }}>{rep.last_participation_exhibition}</div>
                              <div className="text-muted" style={{ fontSize: "0.7rem" }}>{rep.last_role}</div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* ── Live fee warning + acknowledgment (only shown when selection causes excess) ── */}
              {willIncurFee && selectedCount > 0 && (
                <Alert color="warning" className="mt-3 mb-0 p-3 small">
                  <div className="d-flex gap-2 mb-2">
                    <FaDollarSign className="flex-shrink-0 mt-1" size={14} />
                    <div>
                      <strong>Additional Fee Required</strong>
                      <div className="mt-1">
                        {newExcessCount} of your {selectedCount} selected participant(s) will exceed the limit of{" "}
                        <strong>{maxReps}</strong>.
                        Each excess participant incurs a fee of{" "}
                        <strong>{badgeFee.toLocaleString()} {currency}</strong>.
                      </div>
                    </div>
                  </div>

                  {/* Fee breakdown */}
                  <div
                    className="rounded p-2 mb-2 d-flex justify-content-between align-items-center"
                    style={{ background: "rgba(0,0,0,0.05)", fontSize: "0.78rem" }}
                  >
                    <span>
                      {newExcessCount} excess × {badgeFee.toLocaleString()} {currency}
                    </span>
                    <span className="fw-semibold text-danger">
                      = {newExcessFee.toLocaleString()} {currency}
                    </span>
                  </div>

                  {/* Acknowledgment checkbox */}
                  <FormGroup check className="mb-0">
                    <Label check className="small" style={{ cursor: "pointer" }}>
                      <Input
                        type="checkbox"
                        checked={acknowledgeExcessFee}
                        onChange={(e) => setAcknowledgeExcessFee(e.target.checked)}
                        className="me-2"
                      />
                      I acknowledge the additional fee of{" "}
                      <strong>{newExcessFee.toLocaleString()} {currency}</strong> and agree to proceed
                    </Label>
                  </FormGroup>
                </Alert>
              )}
            </ModalBody>

            <ModalFooter className="py-2 px-3">
              <Button color="outline-secondary" size="sm" onClick={toggleSelectExistingModal} disabled={assignLoading}>
                Cancel
              </Button>
              <Button
                color="success"
                size="sm"
                onClick={handleAssignExisting}
                disabled={assignLoading || !canSubmit}
                title={!canSubmit && willIncurFee ? "You must acknowledge the excess fee before proceeding" : undefined}
              >
                {assignLoading ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <FaUserCheck size={11} className="me-1" />
                )}
                {selectedCount > 0
                  ? `Add ${selectedCount} Selected${willIncurFee ? ` (+${newExcessFee.toLocaleString()} ${currency})` : ""}`
                  : "Add Selected"}
              </Button>
            </ModalFooter>
          </Modal>
        )
      })()}

      {/* ════════════════════════════════════════════════════════
          Modal: Self-Registration
      ════════════════════════════════════════════════════════ */}
      <Modal isOpen={selfRegistrationModalOpen} toggle={toggleSelfRegistrationModal} centered size="sm">
        <ModalHeader toggle={toggleSelfRegistrationModal} className="py-2 px-3">
          <FaUserPlus className="me-2" size={15} />
          <span className="fs-6">Add Yourself</span>
        </ModalHeader>
        <ModalBody className="p-3">
          <Alert color="info" className="p-2 small mb-3">
            <FaInfoCircle className="me-1" />
            Register yourself as a participant for <strong>{exhibitor?.name}</strong>.
          </Alert>
          <Form onSubmit={handleSelfRegistration}>
            <div className="border rounded p-2 bg-light mb-3">
              <h6 className="mb-1 small"><FaInfoCircle className="me-1" />What happens:</h6>
              <ul className="mb-0 small ps-3">
                <li>You'll be registered as a participant</li>
                <li>Your account information will be used</li>
                <li>You can edit details later if needed</li>
              </ul>
            </div>
            <div className="d-grid gap-2">
              <Button color="secondary" onClick={toggleSelfRegistrationModal} size="sm" disabled={selfRegistrationLoading}>Cancel</Button>
              <Button color="primary" type="submit" size="sm" disabled={selfRegistrationLoading}>
                {selfRegistrationLoading ? <Spinner size="sm" className="me-1" /> : <FaUserPlus className="me-1" />}
                Add Myself
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          Modal: Add / Edit Staff
      ════════════════════════════════════════════════════════ */}
      <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal} className="py-2 px-3">
          <FaUser className="me-2" size={15} />
          <span className="fs-6">{editingStaff ? "Edit Participant" : "Add New Participant"}</span>
        </ModalHeader>
        <ModalBody className="p-3">
          {formLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" size="sm" />
              <p className="mt-2 small text-muted">Loading participant details…</p>
            </div>
          ) : (
            <>
              {error && <Alert color="danger" className="mb-3 p-2 small">{error}</Alert>}
              {excessFeeWarning && (
                <Alert color="warning" className="mb-3 p-2 small">
                  <FaDollarSign className="me-2" />
                  <strong>Additional Fee Required:</strong>
                  <p className="mb-2 small">{excessFeeWarning.message}</p>
                  <FormGroup check className="mt-2">
                    <Label check className="small">
                      <Input type="checkbox" name="acknowledge_excess_fee" checked={form.acknowledge_excess_fee} onChange={handleInputChange} className="me-2" />
                      I acknowledge the additional fee of {excessFeeWarning.badge_fee} {excessFeeWarning.currency}
                    </Label>
                  </FormGroup>
                </Alert>
              )}
              <Form onSubmit={handleSubmit}>
                <Row className="g-2">
                  <Col md={4}>
                    <FormGroup>
                      <Label className="small mb-1">First Name *</Label>
                      <Input type="text" name="first_name" placeholder="First name" value={form.first_name} onChange={handleInputChange} invalid={hasValidationError("first_name")} required bsSize="sm" />
                      {hasValidationError("first_name") && <FormFeedback className="small">{getValidationError("first_name")}</FormFeedback>}
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label className="small mb-1">Middle Name</Label>
                      <Input type="text" name="middle_name" placeholder="Middle name" value={form.middle_name} onChange={handleInputChange} bsSize="sm" />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label className="small mb-1">Last Name *</Label>
                      <Input type="text" name="last_name" placeholder="Last name" value={form.last_name} onChange={handleInputChange} invalid={hasValidationError("last_name")} required bsSize="sm" />
                      {hasValidationError("last_name") && <FormFeedback className="small">{getValidationError("last_name")}</FormFeedback>}
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="g-2">
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small mb-1">Email *</Label>
                      <Input type="email" name="person_email" placeholder="Email address" value={form.person_email} onChange={handleInputChange} invalid={hasValidationError("email")} required bsSize="sm" />
                      {hasValidationError("email") && <FormFeedback className="small">{getValidationError("email")}</FormFeedback>}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small mb-1">Phone *</Label>
                      <Input type="tel" name="person_phone" placeholder="Phone number" value={form.person_phone} onChange={handleInputChange} invalid={hasValidationError("phone")} required bsSize="sm" />
                      {hasValidationError("phone") && <FormFeedback className="small">{getValidationError("phone")}</FormFeedback>}
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="g-2">
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small mb-1">ID Document Type *</Label>
                      <Input type="select" name="id_doc_type" value={form.id_doc_type} onChange={handleInputChange} invalid={hasValidationError("id_doc_type")} required bsSize="sm">
                        <option value="">Select ID Type</option>
                        {idDocTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </Input>
                      {hasValidationError("id_doc_type") && <FormFeedback className="small">{getValidationError("id_doc_type")}</FormFeedback>}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small mb-1">ID Document Number *</Label>
                      <Input type="text" name="person_id_number" placeholder="ID number" value={form.person_id_number} onChange={handleInputChange} invalid={hasValidationError("id_doc_number")} required bsSize="sm" />
                      {hasValidationError("id_doc_number") && <FormFeedback className="small">{getValidationError("id_doc_number")}</FormFeedback>}
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="g-2">
                  <Col md={12}>
                    <FormGroup>
                      <Label className="small mb-1">Role</Label>
                      <Input type="select" name="role" value={form.role} onChange={handleInputChange} bsSize="sm">
                        <option value="">Select role…</option>
                        {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Photo upload */}
                <FormGroup className="mt-2">
                  <Label className="small mb-1"><FaCamera className="me-1" />Profile Photo *</Label>
                  <div className="border rounded p-2">
                    {currentFiles.has_photo && !form.remove_photo ? (
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          {currentFiles.photo_url && (
                            <img src={currentFiles.photo_url} alt="Current" className="rounded me-2" style={{ width: 36, height: 36, objectFit: "cover" }} />
                          )}
                          <span className="text-success small">Current photo uploaded</span>
                        </div>
                        <Button color="outline-danger" size="sm" onClick={() => handleRemoveFile("photo")} type="button"><FaTrash size={11} /></Button>
                      </div>
                    ) : (
                      <div>
                        <Input type="file" name="photo" accept="image/*" onChange={handleFileChange} invalid={hasValidationError("photo")} required={!editingStaff || (!currentFiles.has_photo && form.remove_photo)} bsSize="sm" />
                        <small className="text-muted">JPG or PNG — Required</small>
                        {hasValidationError("photo") && <div className="invalid-feedback d-block small">{getValidationError("photo")}</div>}
                      </div>
                    )}
                  </div>
                </FormGroup>

                {/* ID doc upload */}
                <FormGroup>
                  <Label className="small mb-1"><FaFileAlt className="me-1" />ID Document (Optional)</Label>
                  <div className="border rounded p-2">
                    {currentFiles.has_id_doc && !form.remove_id_doc ? (
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="text-success small">Current ID document uploaded</span>
                        <Button color="outline-danger" size="sm" onClick={() => handleRemoveFile("id_doc")} type="button"><FaTrash size={11} /></Button>
                      </div>
                    ) : (
                      <div>
                        <Input type="file" name="id_doc_attachment" accept="image/*,.pdf" onChange={handleFileChange} bsSize="sm" />
                        <small className="text-muted">Image or PDF — Optional</small>
                      </div>
                    )}
                  </div>
                </FormGroup>

                {/* Pricing info for new staff */}
                {summary && !editingStaff && (
                  <Alert color="info" className="mt-3 p-2 small">
                    <strong>Pricing:</strong> {summary.exhibition_title} · {summary.stand_category_name} ·
                    Max {summary.max_representatives} · Current {summary.total_staff} ·
                    Fee per excess: {summary.badge_fee} {summary.currency}
                    {summary.total_staff >= summary.max_representatives && summary.max_representatives > 0 && (
                      <span className="text-warning fw-semibold"> — Adding will incur an additional fee</span>
                    )}
                  </Alert>
                )}
              </Form>
            </>
          )}
        </ModalBody>
        <ModalFooter className="py-2 px-3">
          <Button color="outline-secondary" onClick={toggleModal} disabled={formLoading} size="sm">Cancel</Button>
          <Button color="primary" onClick={handleSubmit} disabled={formLoading || (excessFeeWarning && !form.acknowledge_excess_fee)} size="sm">
            {formLoading ? <Spinner size="sm" className="me-1" /> : null}
            {editingStaff ? "Update Participant" : "Add Participant"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default EnhancedStaffManagement